import { useCallback, useDeferredValue, useEffect, useRef, useState, type FocusEvent } from 'react'
import { useLocation } from 'react-router-dom'
import { useAppNavigation } from '../../navigation'
import { getAppModuleIdForPath } from '../../../config/appNavigation'
import { useGlobalSidebarStore } from '../stores/useGlobalSidebarStore'
import { useSidebarMenuWidthStore } from '../stores/useSidebarMenuWidthStore'
import { FAVORITES_RAIL_ITEM, SEARCH_RAIL_ITEM, type ActiveModule } from '../models'
import { useRecordTableOpens } from '../hooks'
import { useHomePageTrail } from '../hooks/useDescribedPaths'
import { useDescribeFavorites, useRecordFavoriteOpens } from '../hooks/useFavorites'
import { useRecordRecentVisits } from '../hooks/useRecentPages'
import { useTableListPreferences } from '../hooks/useTableListPreferences'
import { useSettingsStore } from '@features/settings/stores/useSettingsStore'
import { useUpdateOwnPreference } from '@features/settings/hooks/useUpdateOwnPreference'
import { useRouteNavigate } from '@features/navigation/hooks/useRouteNavigate'
import { useStableCallback } from '@features/shared/hooks/useStableCallback'
import { PopupLayer } from '@features/shared/components/PopupLayer'
import { aboveLayer, layers } from '@features/shared/components/popupLayers'
import { Path } from '../../../routes/paths'
import { HOVER_MENU_CLOSE_DELAY_MS, ME_MENU_CLOSE_GRACE_MS } from '@/styling/tokens.interaction'
import { isWorkingFocus } from '../logic/workingFocus'
import AppSidebar from './AppSidebar'
import ModuleDrawer from './ModuleDrawer'
import { useIsSidebarPinned } from '../hooks/useIsSidebarPinned'
import { useSetSidebarPinned } from '../hooks/useSetSidebarPinned'
import { HomePageDetail } from './HomePageDetail'
import { RailNameCard, type RailName } from './RailNameCard'
import HomePageEditor from './HomePageEditor'
import SidebarSearch from './SidebarSearch'

const SIDEBAR_COLLAPSE_MS = 300
// This browser's sidebar settings from before they became Preferences: the
// folders and the pin are carried over once; how menus opened before a pin is
// retired. Each is removed once read.
const LEGACY_FOLDERS_ENABLED_STORAGE_KEY = 'arvoke-sidebar-folders-enabled'
const LEGACY_PINNED_MODULE_STORAGE_KEY = 'arvoke-sidebar-pinned-module'
const RETIRED_UNPINNED_MENU_MODE_STORAGE_KEY = 'arvoke.sidebarMenu.unpinnedMode'

/** Reads and removes a stored value; `null` when absent or without storage. */
function takeStoredValue(key: string): string | null {
  try {
    const stored = localStorage.getItem(key)
    if (stored !== null) localStorage.removeItem(key)
    return stored
  } catch {
    return null
  }
}

function isColumnDragActive() {
  return document.documentElement.classList.contains('column-dragging-cursor')
}

/** A sidebar row is being dragged: the drawer must not close or switch under it. */
function isRowDragActive() {
  return useGlobalSidebarStore.getState().rowDragActive
}

/**
 * On the page's root while a sidebar row is dragged: nothing under the pointer
 * hovers, lights up, or opens but the list the row is in, which still scrolls
 * (index.css).
 */
const ROW_DRAGGING_CLASS = 'sidebar-row-dragging'

export default function GlobalSidebar() {
  return (
    <PopupLayer layer={aboveLayer(layers.rail)}>
      <GlobalSidebarContent />
    </PopupLayer>
  )
}

function GlobalSidebarContent() {
  const location = useLocation()
  const { modules, settingsNavigationPending, settingsNavigationError } = useAppNavigation()
  const [hoveredModule, setHoveredModule] = useState<ActiveModule | null>(null)
  const [closingModule, setClosingModule] = useState<ActiveModule | null>(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [heldModule, setHeldModule] = useState<ActiveModule | null>(null)
  const sidebarRef = useRef<HTMLDivElement>(null)
  const sidebarMouseDownRef = useRef<MouseEvent | null>(null)
  useEffect(() => {
    takeStoredValue(RETIRED_UNPINNED_MENU_MODE_STORAGE_KEY)
  }, [])
  const { foldersEnabled, tableSort, setFoldersEnabled, setTableSort } = useTableListPreferences()
  const configurationLoaded = useSettingsStore((s) => s.configurationLoaded)
  const navigationMode = useSettingsStore((s) => s.sidebarNavigationMode)
  const updateOwnPreference = useUpdateOwnPreference()
  useRecordTableOpens()
  useRecordFavoriteOpens()
  useRecordRecentVisits()
  // Home's page, worked out ahead so its name card shows it at once.
  const homePageTrail = useHomePageTrail()
  // Favorites' names, worked out before the menu opens.
  useDescribeFavorites()
  const collapseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const menuLeaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const cancelMenuLeave = useCallback(() => {
    if (menuLeaveTimeoutRef.current === null) return
    clearTimeout(menuLeaveTimeoutRef.current)
    menuLeaveTimeoutRef.current = null
  }, [])
  useEffect(() => cancelMenuLeave, [cancelMenuLeave])
  const lastHoveredModuleRef = useRef<ActiveModule | null>(null)
  const selectedModule: ActiveModule = getAppModuleIdForPath(location.pathname) ?? 'tables'
  const isPinned = useIsSidebarPinned()
  const setSidebarPinned = useSetSidebarPinned()
  // The area of a menu item just chosen from a pinned menu, kept as the menu's
  // until the page it opens is current. Pages load in a transition, so the
  // address changes a moment after the click; falling back to the old page's
  // area in between would close the menu or switch it, then switch it back.
  const [chosenFrom, setChosenFrom] = useState<{ module: ActiveModule; path: string } | null>(null)
  const chosenModule = chosenFrom?.path === location.pathname ? chosenFrom.module : null
  const visibleModule =
    heldModule ?? hoveredModule ?? (isPinned ? (chosenModule ?? selectedModule) : closingModule)
  const visibleModuleDefinition = modules.find((module) => module.id === visibleModule)

  // The rail shows names beside its icons always (`expanded`), never
  // (`collapsed`), or while the pointer or keyboard focus is on it (`hover`,
  // floating over the page).
  const expandsOnHover = navigationMode === 'hover'
  const [isRailHovered, setIsRailHovered] = useState(false)
  const [isRailFocused, setIsRailFocused] = useState(false)
  const [isExpansionControlOpen, setIsExpansionControlOpen] = useState(false)
  // The rail item without a menu (Home, AI, Search) the pointer rests on. A
  // hover menu closes meanwhile (but for Search, which opens its own card
  // there), and a pinned one goes blank, grayed out, so the page never moves
  // under the pointer and no other area's menu stands in for one it hasn't
  // got; Home's or AI's name shows over it, beside the rail, however wide the
  // rail is (Search's only on an icons-only rail: it keeps the menu beside it).
  const [railName, setRailName] = useState<RailName | null>(null)
  const isRestingOnMenuless = railName !== null && railName.anchor !== SEARCH_RAIL_ITEM
  // The sidebar's search (SidebarSearch), opened from the rail's Search item:
  // held open (clicked, or typed in) until closed, or shown while the pointer
  // rests on Search or the card itself, as an area's menu is.
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const isSearchRestedOn = railName?.anchor === SEARCH_RAIL_ITEM
  // Home's pencil's card, to change the page Home opens (HomePageEditor).
  const [isHomeEditorOpen, setIsHomeEditorOpen] = useState(false)
  const isSearchShown = isSearchOpen || (isSearchRestedOn && !isHomeEditorOpen)
  // A menu opened by a click on its rail item takes the cursor once it's drawn
  // (ModuleDrawer): in its search, or on its first page.
  const [focusRequest, setFocusRequest] = useState<ActiveModule | null>(null)
  const handleFocusHandled = useCallback(() => setFocusRequest(null), [])
  // Widened on hover, the rail floats over the page, carrying a pinned menu
  // along; it stays wide while the search is open, so the card beside it
  // doesn't move while you type.
  const isRailOpen =
    expandsOnHover &&
    (isRailHovered ||
      isRailFocused ||
      isSearchOpen ||
      isHomeEditorOpen ||
      heldModule !== null ||
      isExpansionControlOpen)
  // Just unpinned, with the pointer still on it: the menu stays open, now as a
  // hover menu, where the pinned card was, until the pointer leaves the
  // sidebar (even where menus otherwise wait for the rail to widen).
  const [isHeldAfterUnpin, setIsHeldAfterUnpin] = useState(false)
  const isRailExpandedInLayout = navigationMode === 'expanded'

  // Where the rail expands on hover, a drawer opens once it has, beside it.
  // Favorites isn't an area, but has a menu like one. Pinned, the card is
  // always there, so the page never moves: an area without a menu (AI) keeps
  // it, blank (ModuleDrawer).
  const visibleHasMenu =
    visibleModule === FAVORITES_RAIL_ITEM ||
    (visibleModuleDefinition !== undefined && visibleModuleDefinition.drawer !== 'none')
  const isModuleDrawerOpen =
    isPinned ||
    (isDrawerOpen &&
      (!expandsOnHover || isRailOpen || isHeldAfterUnpin) &&
      // Search has no menu but opens its own card there, so resting on it
      // leaves a hover menu where it is too.
      (railName === null || railName.anchor === SEARCH_RAIL_ITEM) &&
      visibleHasMenu)
  // The rail widens and the area's tile lights at once; its menu follows in a
  // render of its own, so a long one (Tables draws its whole list) never holds
  // them up. Moving to another area keeps the shown menu until the next is
  // ready; closing is at once.
  const drawerModule = useDeferredValue(visibleModule)
  const isDrawerOpenDeferred = useDeferredValue(isModuleDrawerOpen)
  const isDrawerShown = isModuleDrawerOpen && isDrawerOpenDeferred
  const isDrawerInLayout = isDrawerShown && isPinned
  // By the menu in the card (it follows a render behind), so it never shows
  // one area's menu with another's blankness.
  const drawerHasMenu =
    drawerModule === FAVORITES_RAIL_ITEM ||
    modules.find((module) => module.id === drawerModule)?.drawer !== 'none'
  const isDrawerBlank = isPinned && (isRestingOnMenuless || !drawerHasMenu)
  // An area without a menu (AI) leaves the pinned card the last menu it
  // showed, idle under a veil, rather than an empty card.
  const [lastMenuModule, setLastMenuModule] = useState<ActiveModule | null>(null)
  if (drawerHasMenu && drawerModule !== null && drawerModule !== lastMenuModule) {
    setLastMenuModule(drawerModule)
  }
  const cardModule = drawerHasMenu ? drawerModule : (lastMenuModule ?? drawerModule)
  // Every menu is as wide as the widest one the user can open, so a pinned
  // card always fills the page's room for it and the page never moves as it
  // switches menus.
  // Dragged wider or narrower from the pinned card's edge, every menu takes
  // that width instead (useSidebarMenuWidthStore).
  const customMenuWidth = useSidebarMenuWidthStore((state) => state.width)
  const isMenuResizing = useSidebarMenuWidthStore((state) => state.isResizing)
  const menuWidth =
    customMenuWidth !== null
      ? `clamp(var(--sidebar-drawer-min-width), ${customMenuWidth}px, var(--sidebar-drawer-max-width))`
      : modules.some((module) => module.drawer === 'table-tree')
        ? 'var(--sidebar-drawer-width)'
        : 'var(--sidebar-flyout-width)'
  const railLayoutWidth = isRailExpandedInLayout
    ? 'var(--sidebar-width-expanded)'
    : 'var(--sidebar-width-collapsed)'

  // Keep the current card across the rail's gaps and the next item's hover
  // intent. Home and Search then exchange in one commit instead of clearing
  // Home before Search has had a chance to open.
  const railLeaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const cancelRailLeave = useCallback(() => {
    if (railLeaveTimerRef.current === null) return
    clearTimeout(railLeaveTimerRef.current)
    railLeaveTimerRef.current = null
  }, [])
  useEffect(() => cancelRailLeave, [cancelRailLeave])
  const leaveRailItem = useCallback(
    (anchor: string) => {
      cancelRailLeave()
      railLeaveTimerRef.current = setTimeout(() => {
        railLeaveTimerRef.current = null
        // A late leave from an old item never closes its replacement.
        setRailName((current) => (current?.anchor === anchor ? null : current))
      }, HOVER_MENU_CLOSE_DELAY_MS)
    },
    [cancelRailLeave]
  )
  const closeSearch = useCallback(() => {
    cancelRailLeave()
    setIsSearchOpen(false)
    setRailName((current) => (current?.anchor === SEARCH_RAIL_ITEM ? null : current))
  }, [cancelRailLeave])
  const holdSearch = useCallback(() => {
    cancelMenuLeave()
    setHeldModule(null)
    cancelRailLeave()
    setHoveredModule(null)
    setIsHomeEditorOpen(false)
    setIsSearchOpen(true)
  }, [cancelRailLeave, cancelMenuLeave])
  const leaveSearch = useCallback(() => leaveRailItem(SEARCH_RAIL_ITEM), [leaveRailItem])
  const nameRailItem = useCallback(
    (name: RailName | null) => {
      if (heldModule !== null) return
      cancelRailLeave()
      setRailName(name)
    },
    [cancelRailLeave, heldModule]
  )
  // Home's pencil: a card beside Home with the Home Page picker.
  const openHomeEditor = useCallback(() => {
    cancelMenuLeave()
    cancelRailLeave()
    setHeldModule(null)
    setRailName(null)
    setHoveredModule(null)
    setIsSearchOpen(false)
    setIsHomeEditorOpen(true)
  }, [cancelMenuLeave, cancelRailLeave])
  const closeHomeEditor = useCallback(() => setIsHomeEditorOpen(false), [])
  const leaveRailName = useCallback(() => {
    if (railName) leaveRailItem(railName.anchor)
  }, [leaveRailItem, railName])

  // Opens an area's menu beside its rail item: on a hover, or at once on a click.
  const openModuleMenu = (module: ActiveModule) => {
    cancelMenuLeave()
    cancelRailLeave()
    setRailName(null)
    setHeldModule(null)
    clearCollapseTimeout()
    setClosingModule(null)
    // A new area under the pointer takes over from one just chosen.
    setChosenFrom(null)
    lastHoveredModuleRef.current = module
    setHoveredModule(module)
    setIsDrawerOpen(true)
  }

  const clearCollapseTimeout = () => {
    if (collapseTimeoutRef.current !== null) {
      clearTimeout(collapseTimeoutRef.current)
      collapseTimeoutRef.current = null
    }
  }

  const collapseSidebar = (moduleToPersist: ActiveModule | null) => {
    cancelMenuLeave()
    setHeldModule(null)
    setIsRailHovered(false)
    setIsHeldAfterUnpin(false)
    if (isPinned) {
      setHoveredModule(null)
      return
    }

    clearCollapseTimeout()
    setClosingModule(moduleToPersist)
    setIsDrawerOpen(false)
    setHoveredModule(null)
    collapseTimeoutRef.current = setTimeout(() => {
      setClosingModule(null)
      collapseTimeoutRef.current = null
    }, SIDEBAR_COLLAPSE_MS)
  }

  const handleTogglePin = (module: ActiveModule) => {
    cancelMenuLeave()
    setHeldModule(null)
    void setSidebarPinned(!isPinned)
    clearCollapseTimeout()
    setClosingModule(null)
    setIsDrawerOpen(true)
    lastHoveredModuleRef.current = module
    if (isPinned) {
      // The menu stays as the area's hover menu, where it is.
      setHoveredModule(module)
      setIsHeldAfterUnpin(true)
    }
  }

  useEffect(() => {
    if (!configurationLoaded) return
    if (
      takeStoredValue(LEGACY_FOLDERS_ENABLED_STORAGE_KEY) === 'false' &&
      useSettingsStore.getState().sidebarTableFolders
    ) {
      void updateOwnPreference('sidebar_table_folders', 'sidebarTableFolders', false)
    }
    if (
      takeStoredValue(LEGACY_PINNED_MODULE_STORAGE_KEY) &&
      useSettingsStore.getState().sidebarMenuMode !== 'pinned'
    ) {
      void updateOwnPreference('sidebar_menu_mode', 'sidebarMenuMode', 'pinned')
    }
  }, [configurationLoaded, updateOwnPreference])

  useEffect(() => clearCollapseTimeout, [])

  const dismissHeldMenu = useStableCallback(() => collapseSidebar(heldModule))
  useEffect(() => {
    if (heldModule === null) return
    const dismissOutside = (event: MouseEvent) => {
      // React capture follows portals: an organization selector's options and
      // search are still inside the sidebar even though their DOM is elsewhere.
      if (event === sidebarMouseDownRef.current) return
      if (sidebarRef.current?.contains(event.target as Node)) return
      dismissHeldMenu()
    }
    document.addEventListener('mousedown', dismissOutside)
    return () => document.removeEventListener('mousedown', dismissOutside)
  }, [heldModule, dismissHeldMenu])

  useEffect(() => {
    if (heldModule === null) return
    const dismiss = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return
      if (document.querySelector('[data-escape-consumer]')) return
      dismissHeldMenu()
    }
    document.addEventListener('keydown', dismiss)
    return () => document.removeEventListener('keydown', dismiss)
  }, [heldModule, dismissHeldMenu])

  const routeNavigate = useRouteNavigate()

  // Stable, so the memoized table list doesn't re-render on every navigation.
  const handleItemSelect = useStableCallback(() => {
    setHeldModule(null)
    if (isPinned) {
      if (visibleModule) setChosenFrom({ module: visibleModule, path: location.pathname })
      setHoveredModule(null)
      return
    }
    collapseSidebar(visibleModule)
  })

  const openTableSettings = useStableCallback((tableId: string) => {
    routeNavigate(Path.tableDefaultViewRecords(tableId), {
      state: { openTableSettings: true },
    })
  })

  // A row let go of off the sidebar closes its hover menu once it has dropped.
  // The sidebar hears no pointer while a row is dragged, so where the pointer
  // was last is what counts.
  const collapseAfterRowDrag = useStableCallback((pointer: { x: number; y: number } | null) => {
    if (!pointer) return
    if (document.elementFromPoint(pointer.x, pointer.y)?.closest('[data-app-sidebar]')) return
    collapseSidebar(hoveredModule ?? lastHoveredModuleRef.current)
  })

  useEffect(() => {
    const root = document.documentElement
    let pointer: { x: number; y: number } | null = null
    const trackPointer = (event: PointerEvent) => {
      pointer = { x: event.clientX, y: event.clientY }
    }
    const endRowDrag = () => {
      root.classList.remove(ROW_DRAGGING_CLASS)
      window.removeEventListener('pointermove', trackPointer, true)
    }
    const unsubscribe = useGlobalSidebarStore.subscribe((state, previous) => {
      if (state.rowDragActive === previous.rowDragActive) return
      if (state.rowDragActive) {
        pointer = null
        root.classList.add(ROW_DRAGGING_CLASS)
        window.addEventListener('pointermove', trackPointer, true)
        return
      }
      endRowDrag()
      collapseAfterRowDrag(pointer)
    })
    return () => {
      unsubscribe()
      endRowDrag()
    }
  }, [collapseAfterRowDrag])

  // The open menu's card (or the search's), for the rail's read of where the
  // pointer is heading.
  const getMenuEdge = useCallback(() => {
    // Search's card sits over a pinned menu while it's open.
    const card =
      sidebarRef.current?.querySelector<HTMLElement>('[data-rail-menu="search"]') ??
      sidebarRef.current?.querySelector<HTMLElement>('[data-rail-menu]')
    const rect = card?.getBoundingClientRect()
    return rect && rect.width > 0 ? { left: rect.left, top: rect.top, bottom: rect.bottom } : null
  }, [])

  const handleBlur = (event: FocusEvent<HTMLElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      setIsRailFocused(false)
    }
  }

  return (
    <div
      ref={sidebarRef}
      data-app-sidebar
      // Its width is what the page makes room for: the rail as it rests, and a
      // pinned drawer.
      // Dragging the pinned menu's edge, the page follows the pointer at once.
      className={`relative z-[var(--layer-rail)] h-full shrink-0 overflow-visible ${
        isMenuResizing ? 'transition-none' : 'transition-[width] duration-200'
      }`}
      style={{
        ['--sidebar-menu-width' as string]: menuWidth,
        width: isDrawerInLayout
          ? // The pinned menu's card, and its gap from the page card.
            `calc(${railLayoutWidth} + var(--sidebar-menu-width) + var(--page-card-gap))`
          : railLayoutWidth,
      }}
      onBlur={handleBlur}
      onMouseDownCapture={(event) => {
        sidebarMouseDownRef.current = event.nativeEvent
      }}
      onClickCapture={(event) => {
        if (heldModule !== null && (event.target as Element).closest('a[href]')) {
          dismissHeldMenu()
        }
      }}
      onMouseEnter={() => {
        if (isColumnDragActive()) return
        cancelMenuLeave()
        clearCollapseTimeout()
        setClosingModule(null)
        setIsDrawerOpen(true)
      }}
      onMouseLeave={() => {
        // A row being dragged decides once it drops (collapseAfterRowDrag).
        if (isColumnDragActive() || isRowDragActive()) return
        if (heldModule !== null) {
          setIsRailHovered(false)
          return
        }
        const leavingModule = hoveredModule ?? lastHoveredModuleRef.current
        if (leavingModule === 'me') {
          cancelMenuLeave()
          menuLeaveTimeoutRef.current = setTimeout(() => {
            menuLeaveTimeoutRef.current = null
            collapseSidebar('me')
          }, ME_MENU_CLOSE_GRACE_MS)
          return
        }
        collapseSidebar(leavingModule)
      }}
    >
      {/* The rail and its drawer, one surface: past the width above it floats
          over the page, edged like the page card (app-rail-floating-edge). As
          wide as the rail: the menus are placed from it. A pinned menu rides
          beside the widened rail over the page, as a hover menu does; the
          page stays where it rests beside the narrow rail and the menu. */}
      <div
        className={`absolute inset-y-0 left-0 flex ${isRailOpen ? 'app-rail-floating-edge' : ''}`}
      >
        <div
          // Above the menus beside it, where they meet.
          className="relative z-10 flex h-full"
          // Only the rail itself expands it, the moment the pointer reaches it;
          // working in a pinned drawer leaves it as it is.
          onMouseEnter={() => {
            if (expandsOnHover) setIsRailHovered(true)
          }}
          onFocus={(event) => {
            if (expandsOnHover && isWorkingFocus(event.target)) setIsRailFocused(true)
          }}
        >
          <AppSidebar
            modules={modules}
            selectedModule={selectedModule}
            hoveredModule={hoveredModule}
            isMenuShown={isDrawerShown || isSearchShown}
            getMenuEdge={getMenuEdge}
            onModuleHover={(module) => {
              if (heldModule !== null) return
              if (isColumnDragActive() || isRowDragActive()) return
              // While the search is open, the rail's menus stay closed.
              if (module !== null && (isSearchOpen || isHomeEditorOpen)) return

              if (module === null) {
                setHoveredModule(null)
                return
              }
              openModuleMenu(module)
            }}
            onModuleClick={(module, { keyboard }) => {
              if (isColumnDragActive() || isRowDragActive()) return
              // Clicking the AI Assistant starts a new chat, as its menu's New
              // chat does. Enter still opens the menu, so the saved
              // conversations stay in reach from the keyboard.
              if (module === 'arvoke-ai' && !keyboard) {
                routeNavigate(Path.aiChat)
                handleItemSelect()
                return
              }
              openModuleMenu(module)
              setFocusRequest(module)
            }}
            isExpanded={isRailExpandedInLayout || isRailOpen}
            onRailNameChange={nameRailItem}
            onRailNameEnter={cancelRailLeave}
            onRailNameLeave={leaveRailItem}
            namedItem={isRestingOnMenuless ? railName.anchor : null}
            searchOpen={isSearchShown}
            onSearchClick={() => (isSearchOpen ? closeSearch() : holdSearch())}
            onExpansionOpenChange={(open) => {
              setIsExpansionControlOpen(open)
              if (!open) return
              cancelMenuLeave()
              clearCollapseTimeout()
              setHeldModule(null)
              setHoveredModule(null)
              setRailName(null)
              closeSearch()
              setIsHomeEditorOpen(false)
            }}
          />
        </div>
        <ModuleDrawer
          activeModule={cardModule}
          modules={modules}
          settingsNavigationPending={settingsNavigationPending}
          settingsNavigationError={settingsNavigationError}
          isOpen={isDrawerShown}
          isPinned={isPinned}
          isFloating={isRailOpen}
          foldersEnabled={foldersEnabled}
          tableSort={tableSort}
          onToggleFolders={() => setFoldersEnabled(!foldersEnabled)}
          onEnableFolders={() => setFoldersEnabled(true)}
          onTableSortChange={setTableSort}
          onTogglePin={handleTogglePin}
          onItemSelect={handleItemSelect}
          onSettingChange={() => {
            cancelMenuLeave()
            clearCollapseTimeout()
            setRailName(null)
            setHeldModule('me')
            setIsDrawerOpen(true)
          }}
          onOpenTableSettings={openTableSettings}
          blank={isDrawerBlank}
          underPointer={hoveredModule !== null && hoveredModule === drawerModule}
          focusRequested={focusRequest !== null && focusRequest === drawerModule}
          onFocusHandled={handleFocusHandled}
        />
        <SidebarSearch
          open={isSearchShown}
          held={isSearchOpen}
          onHold={holdSearch}
          onEnter={cancelRailLeave}
          onLeave={leaveSearch}
          onClose={closeSearch}
        />
        <HomePageEditor open={isHomeEditorOpen} onClose={closeHomeEditor} />
        {/* Replace the whole banner in Search's paint, including any part
            wider than Search. A genuine leave still uses its normal fade. */}
        {!isSearchShown && !isHomeEditorOpen && (
          <RailNameCard
            name={isRestingOnMenuless || navigationMode === 'collapsed' ? railName : null}
            details={{ home: <HomePageDetail trail={homePageTrail} onChange={openHomeEditor} /> }}
            onEnter={cancelRailLeave}
            onLeave={leaveRailName}
          />
        )}
      </div>
    </div>
  )
}
